import { Component, ComponentInterface, Prop, Watch } from "@stencil/core";
import api from "../../utils/api";
import serializeForm from "../../utils/serializeForm";
import waitForHydrated from "../../utils/waitForHydrated";
import { MymachineItem, MymachineResponse } from "./mymachine-response";
import row from "./row";
import tableTooltip from "./tableTooltip";
import tile from "./tile";

@Component({
    tag: "ux-iws-data-controller-mymachine",
    styleUrl: "data-controller-mymachine.scss",
})
export class DataControllerMymachine implements ComponentInterface {
    /** endpoint where data can be requested */
    @Prop() url: string = "";
    /** CSS-style selector for the select with all the sorting options */
    @Prop() sortFormSelector: string = "";
    /** CSS-style selector for the filter */
    @Prop() filterSelector: string = "";
    /** CSS-style selector for the table */
    @Prop() tableSelector: string = "";
    /** CSS-style selector for the tiles container element */
    @Prop() tilesContainerSelector: string = "";
    /** CSS-style selector for the element where the total number of machines should be placed */
    @Prop() totalNumberSelector: string = "";
    /** CSS-style selector for the element where the number of filtered machines should be placed */
    @Prop() filteredNumberSelector: string = "";
    /** CSS-style selector for the scroll-sensor */
    @Prop() scrollSensorSelector: string = "";
    /** Text that should be displayed when multiple shop links are displayed */
    @Prop() chooseShopText: string = "";
    /** Indicates if software updates should be rendered */
    @Prop() softwareUpdatesAvailable: boolean = false;

    /** can the current user create service cases or not */
    private serviceCaseCreationDisabled: boolean = false;
    /** header text for case creation disabled explaination tooltip */
    private serviceCaseCreateDisabledHeaderText: string;
    /** body text for case creation disabled explaination tooltip */
    private serviceCaseCreateDisabledBodyText: string;
    /** select input for changing the sort-order */
    private sortForm: HTMLFormElement | null = null;
    /** element that contains all filters */
    private filter: HTMLUxFilterElement | null = null;
    /** form element for filter */
    private filterForm: HTMLFormElement | null = null;
    /** table element */
    private table: HTMLTableElement | null = null;
    /** tooltip container element for the table view */
    private tooltipContainer: HTMLDivElement | null = null;
    /** table body element where the data should be put */
    private tableBody: HTMLTableSectionElement | null = null;
    /** DOM element where all the tiles should be put */
    private tilesContainer: HTMLElement | null = null;
    /** NodeList of DOM elements where the number of filtered machines should be put */
    private filteredNumberElements: NodeList | null = null;
    /** NodeList of DOM elements where the total number of machines should be put */
    private totalNumberElements: NodeList | null = null;
    /** URL for getting the next page */
    private nextPage: string = "";
    /** DOM element where all the tiles should be put */
    private scrollSensor: HTMLUxInfiniteScrollSensorElement | null = null;
    /** element that shows active filters */
    private activeFiltersContainer: HTMLElement | null = null;
    /** element that shows label for active filters */
    private activeFiltersLabel: HTMLElement | null = null;
    /** spinner element */
    private spinner = null;
    /** inital request */
    private initialRequestState: boolean = true;

    componentDidLoad() {
        this.setSortForm();
        this.setFilter();
        this.setTilesContainer();
        this.setTable();
        this.setScrollSensor();
        this.setTotalNumberElements();
        this.setFilteredNumberElements();
        this.setSpinnerOverlay();
        this.requestData(this.url);
    }

    private setSpinnerOverlay() {
        if (this.spinner === null) {
            this.spinner = document.createElement("ux-spinner-overlay");
            document.body.append(this.spinner);
        }
        this.spinner.opened = true;
    }

    /** connect sort select */
    @Watch("sortFormSelector")
    setSortForm() {
        if (this.sortFormSelector === "") return;
        this.sortForm = document.querySelector(
            this.sortFormSelector
        ) as HTMLFormElement;
        this.sortForm.addEventListener("submit", (e) => e.preventDefault());
        this.sortForm.addEventListener("change", () => {
            this.requestData(this.url);
            this.syncSortStateToTable();
        });
    }

    private syncSortStateToTable() {
        if (this.table === null || this.sortForm === null) return;
        const { value } = this.sortForm.querySelector("select");
        const prevSorted = this.table.querySelector(
            `ux-table-sort[sorted-direction="desc"], ux-table-sort[sorted-direction="asc"]`
        ) as HTMLUxTableSortElement;
        if (prevSorted !== null) {
            prevSorted.sortedDirection = "";
        }
        const ascSort = this.table.querySelector(
            `ux-table-sort[asc-name="${value}"]`
        ) as HTMLUxTableSortElement;
        const descSort = this.table.querySelector(
            `ux-table-sort[desc-name="${value}"]`
        ) as HTMLUxTableSortElement;
        if (ascSort !== null) {
            ascSort.sortedDirection = "asc";
        }
        if (descSort !== null) {
            descSort.sortedDirection = "desc";
        }
    }

    /** connect filter */
    @Watch("filterSelector")
    setFilter() {
        if (this.filterSelector === "") return;
        this.filter = document.querySelector(
            this.filterSelector
        ) as HTMLUxFilterElement;
        if (this.filter === null) return;
        waitForHydrated(this.filter, () => this.setFilterForm());
        this.filter.addEventListener("uxFilterChange", () => {
            this.setActiveFilterLabelVisibility();
            this.requestData(this.url);
        });
    }

    /** connect total number */
    @Watch("totalNumberSelector")
    setTotalNumberElements() {
        if (this.totalNumberSelector === "") return;
        this.totalNumberElements = document.querySelectorAll(
            this.totalNumberSelector
        );
    }

    /** connect filtered number */
    @Watch("filteredNumberSelector")
    setFilteredNumberElements() {
        if (this.filteredNumberSelector === "") return;
        this.filteredNumberElements = document.querySelectorAll(
            this.filteredNumberSelector
        );
    }

    private setFilterForm() {
        this.filterForm = this.filter.querySelector("form") as HTMLFormElement;
        this.filterForm?.addEventListener("submit", (e) => e.preventDefault());
        this.activeFiltersContainer = this.filter.querySelector(".ux-tag-list");
        if (this.activeFiltersContainer !== null) {
            this.activeFiltersLabel =
                this.activeFiltersContainer.parentElement.querySelector(
                    ".ux-label"
                );
        }
        this.setActiveFilterLabelVisibility();
    }

    private setActiveFilterLabelVisibility() {
        if (
            this.activeFiltersLabel === null ||
            this.activeFiltersContainer === null
        )
            return;
        if (this.activeFiltersContainer.querySelectorAll("ux-tag").length > 0) {
            this.activeFiltersLabel.style.display = "";
        } else {
            this.activeFiltersLabel.style.display = "none";
        }
    }

    private updateNumbers(filtered: number, total: number): void {
        writeToElements(this.filteredNumberElements, filtered.toString());
        writeToElements(this.totalNumberElements, total.toString());
    }

    /** connect table */
    @Watch("tableSelector")
    setTable() {
        if (this.tableSelector === "") return;
        this.table = document.querySelector(
            this.tableSelector
        ) as HTMLTableElement;

        this.tableBody = this.table?.querySelector("tbody");
        if (this.table !== null && this.sortForm !== null) {
            this.table.addEventListener("uxTableSort", (e: any) => {
                const { name } = e.detail;
                if (name === "") return;
                this.sortForm.querySelector("select").value = name;
                this.requestData(this.url);
            });
            this.syncSortStateToTable();
        }
        if (this.table !== null) {
            const uxTable = this.table.closest("ux-table");
            this.tooltipContainer = document.createElement("div");
            this.tooltipContainer.className =
                "mymachine-table-tooltip-container";
            uxTable.after(this.tooltipContainer);
        }
    }

    /** connect scroll-sensor */
    @Watch("scrollSensorSelector")
    setScrollSensor() {
        if (this.scrollSensorSelector === "") return;
        this.scrollSensor = document.querySelector(
            this.scrollSensorSelector
        ) as HTMLUxInfiniteScrollSensorElement;
        if (this.scrollSensor !== null) {
            this.scrollSensor.addEventListener(
                "uxInfiniteScrollSensorScrolledIntoView",
                () => {
                    this.requestData(this.nextPage);
                }
            );
        }
    }

    /** connect tileContainer */
    @Watch("tilesContainerSelector")
    setTilesContainer() {
        if (this.tilesContainerSelector === "") return;
        this.tilesContainer = document.querySelector(
            this.tilesContainerSelector
        ) as HTMLElement;
    }

    /** post filter and sort settings to get data from the endpoint */
    @Watch("url")
    initialRequest() {
        this.requestData(this.url);
    }

    /** Send the real request and handle response */
    private requestData(url: string) {
        if (url === "") return;
        this.spinner.opened = true;
        if (url === this.url) this.clearExistingData();

        const body = this.initialRequestState
            ? "filters=restoreFromSession"
            : serializeForm(this.sortForm) +
              "&" +
              serializeForm(this.filterForm);

        if (isMockUrl(url)) {
            console.log("filter:", body);
        }
        const options = isMockUrl(url)
            ? {}
            : {
                  method: "POST",
                  body,

                  headers: {
                      "Content-Type":
                          "application/x-www-form-urlencoded;charset=UTF-8",
                  },
              };

        try {
            api<MymachineResponse>(url, options)
                .then((data) => {
                    if (this.url === url) {
                        this.clearExistingData();
                        this.replaceFilter(data.filterHTML);
                    }

                    this.setUserCanCreateServiceCase(data.isCreateCaseAllowed);
                    this.serviceCaseCreateDisabledHeaderText =
                        data.disabledCreateCaseHeaderText;
                    this.serviceCaseCreateDisabledBodyText =
                        data.disabledCreateCaseBodyText;

                    this.appendItemsToTable(data.items);
                    this.appendItemsToTiles(data.items);
                    this.updateNumbers(data.filteredCount, data.totalCount);
                    this.nextPage = data.nextPage || "";
                    if (this.nextPage !== "" && this.scrollSensor !== null) {
                        this.scrollSensor.hasFired = false;
                    }
                })
                .finally(() => {
                    this.spinner.opened = false;
                    this.initialRequestState = false;
                });
        } catch (e) {
            console.error(e);
            this.spinner.opened = false;
        }
    }

    private replaceFilter(html: string | undefined) {
        if (
            typeof html === "string" &&
            html.length > 0 &&
            this.filter !== null
        ) {
            const parent = this.filter.parentElement;
            if (parent !== null) {
                parent.style.height = `${parent.offsetHeight}px`;
            }
            this.filter.outerHTML = html;
            this.setFilter();
            setTimeout(() => {
                parent.style.height = "auto";
            }, 200);
        }
    }

    private clearExistingData() {
        if (this.tableBody !== null) this.tableBody.textContent = "";
        if (this.tooltipContainer !== null)
            this.tooltipContainer.textContent = "";
        if (this.tilesContainer !== null) this.tilesContainer.textContent = "";
        this.setUserCanCreateServiceCase(false);
    }

    private appendItemsToTable(items: MymachineItem[]) {
        if (this.tableBody === null) return;
        this.tableBody.insertAdjacentHTML(
            "beforeend",
            items.map(row(this.softwareUpdatesAvailable)).join("")
        );
        this.tooltipContainer.insertAdjacentHTML(
            "beforeend",
            items.map(tableTooltip).join("")
        );
    }

    private setUserCanCreateServiceCase(isAllowed: boolean) {
        this.serviceCaseCreationDisabled = !isAllowed;
    }

    private appendItemsToTiles(items: MymachineItem[]) {
        if (this.tilesContainer === null) return;
        this.tilesContainer.insertAdjacentHTML(
            "beforeend",
            items
                .map(
                    tile(
                        this.chooseShopText,
                        this.serviceCaseCreationDisabled,
                        this.serviceCaseCreateDisabledHeaderText,
                        this.serviceCaseCreateDisabledBodyText,
                        this.softwareUpdatesAvailable
                    )
                )
                .join("")
        );
    }

    render() {
        return null;
    }
}

function isMockUrl(url: string) {
    return url.indexOf("mock-data/") > -1;
}

function writeToElements(nodes: NodeList | null, text: string): void {
    if (nodes === null) return;
    [].forEach.call(
        nodes,
        (element: HTMLElement) => (element.textContent = text)
    );
}
